import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { partnerApiService } from '../../services/api';
import {
  CODE_ALREADY_REGISTERED,
  CODE_PARTNER_NOT_FOUND,
  apiErrorCode,
  apiErrorMessage,
} from '../../services/apiErrors';
import { formatPhone, isCompletePhone, normalizePhone } from '../../utils/phone';
import { AUTH_PATHS } from './authPaths';
import { Notice, Spinner } from './AuthParts';
import { useLanguage } from '../../contexts/LanguageContext';
import './auth.css';

export type AuthMode = 'login' | 'registration';

interface PhoneStepFormProps {
  mode: AuthMode;
  /** Номер, перенесённый с соседней двери. */
  initialPhone?: string;
  /** Почему человек оказался здесь, а не там, куда шёл. */
  notice?: string;
  onCodeSent: (phone: string) => void;
  /** Дверь не та: номер уходит в соседнюю форму вместе с объяснением. */
  onWrongDoor: (phone: string, message: string) => void;
}

export const PhoneStepForm: React.FC<PhoneStepFormProps> = ({
  mode,
  initialPhone = '',
  notice,
  onCodeSent,
  onWrongDoor,
}) => {
  const { t } = useLanguage();
  const [phone, setPhone] = useState(() => (initialPhone ? formatPhone(initialPhone) : ''));
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const copy = mode === 'registration' ? {
    title: t.auth.registerTitle,
    subtitle: t.auth.registerSubtitle,
    submit: t.auth.phoneButton,
    otherDoorText: t.auth.otherDoorHasCabinet,
    otherDoorLabel: t.auth.otherDoorLogin,
    otherDoorPath: AUTH_PATHS.login,
    wrongDoorCode: CODE_ALREADY_REGISTERED,
  } : {
    title: t.auth.loginTitle,
    subtitle: t.auth.loginSubtitle,
    submit: t.auth.phoneButton,
    otherDoorText: t.auth.otherDoorNoCabinet,
    otherDoorLabel: t.auth.otherDoorRegister,
    otherDoorPath: AUTH_PATHS.register,
    wrongDoorCode: CODE_PARTNER_NOT_FOUND,
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const normalized = normalizePhone(phone);
    if (!isCompletePhone(normalized)) {
      setError(t.auth.phoneValidationError);
      return;
    }

    setIsLoading(true);
    try {
      if (mode === 'registration') {
        await partnerApiService.sendRegistrationCode({ phone: normalized });
      } else {
        await partnerApiService.sendLoginCode({ phone: normalized });
      }
      onCodeSent(normalized);
    } catch (err) {
      if (apiErrorCode(err) === copy.wrongDoorCode) {
        onWrongDoor(normalized, apiErrorMessage(err));
        return;
      }
      setError(apiErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  const canSubmit = isCompletePhone(phone) && !isLoading;

  return (
    <div className="m3-auth-screen">
      <div className="m3-auth-card">
        {notice && <Notice>{notice}</Notice>}

        <h1 className="m3-headline">{copy.title}</h1>
        <p className="m3-body" style={{ marginBottom: 28 }}>
          {copy.subtitle}
        </p>

        <form onSubmit={handleSubmit} className="qp-auth-form">
          <div className="qp-auth-control">
            <label className={`m3-field${error ? ' m3-field--error' : ''}`} htmlFor="phone">
              <input
                id="phone"
                name="phone"
                type="tel"
                inputMode="numeric"
                value={phone}
                onChange={(e) => setPhone(formatPhone(e.target.value))}
                placeholder={t.auth.phonePlaceholder}
                className="m3-field-input"
                required
                disabled={isLoading}
                autoComplete="tel"
                aria-invalid={error ? true : undefined}
              />
              <span className="m3-field-label">{t.auth.phoneLabel}</span>
            </label>
            {error && (
              <span className="m3-field-support m3-field-support--error" role="alert">
                {error}
              </span>
            )}
          </div>

          <button type="submit" disabled={!canSubmit} className="m3-btn-filled m3-btn-filled--block">
            {isLoading && <Spinner />}
            {isLoading ? t.common.loading : copy.submit}
          </button>
        </form>

        <p className="m3-body qp-auth-footnote">{t.auth.phoneNotice}</p>

        <p className="m3-body qp-auth-otherdoor">
          {copy.otherDoorText}{' '}
          <Link to={copy.otherDoorPath} state={{ phone: normalizePhone(phone) || undefined }}>
            {copy.otherDoorLabel}
          </Link>
        </p>
      </div>
    </div>
  );
};
